/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import {
    IBuyboxAddToCartViewProps,
    IBuyboxAddToOrderTemplateViewProps,
    IBuyboxAddToWishlistViewProps,
    IBuyboxCallbacks,
    IBuyboxData,
    IBuyboxFindInStoreViewProps,
    IBuyboxKeyInPriceViewProps,
    IBuyboxProductConfigureDropdownViewProps,
    IBuyboxProductConfigureViewProps,
    IBuyboxProductQuantityViewProps,
    IBuyboxProps,
    IBuyboxShopSimilarLookViewProps,
    IBuyboxState,
    IBuyboxViewProps,
    IDropdownItemProps
} from '@msdyn365-commerce-modules/buybox';
import * as Msdyn365 from '@msdyn365-commerce/core';
import { Image } from '@msdyn365-commerce/core';
import { ITelemetryContent, Module, Node, Waiting } from '@msdyn365-commerce-modules/utilities';
import IncrementalQuantity from './components/incremental-quantity/IncrementalQuantity';
import * as React from 'react';
import { AttributeValue, ProductDimension, ProductDimensionValueInventoryAvailability } from '@msdyn365-commerce/retail-proxy';
import { generateImageUrl } from '@msdyn365-commerce-modules/retail-actions';
import { ICommerceApiSettings } from '@msdyn365-commerce/core-internal';

import {
    IBuyboxProps as IBuyboxExtensionProps,
    IBuyboxResources as IBuyboxExtensionResources
} from '../definition-extensions/buybox.ext.props.autogenerated';
import { IBuyboxData as IBuyboxExtensionData } from './buybox.data';
import { useAttributeState } from '../../../utilities/custom-hooks';
import { getQuantityThreshold, itemCodeRegex, percentageValue } from '../../../utilities/helpers';
import { sWGGetInventoryDataAsync } from '../../../custom-proxy/RetailExtension/DataActionExtension.g';
import { ISWGInventoryData } from '../../../custom-proxy/RetailExtension/DataServiceEntities.g';

import { UncontrolledTooltip } from '@msdyn365-commerce-modules/utilities';
import { IErrorState } from '@msdyn365-commerce-modules/buybox/dist/types/common/buyboxInterface';
import { splitQuantityThresholdMessage } from './../../../utilities/helpers';
import { RichTextComponent } from '@msdyn365-commerce/core';
import get from 'lodash/get';
import set from 'lodash/set';
// import InlineZoomImageComponent from './components/inline-zoom-image.component';
// import ReactImageZoom from 'react-image-zoom';
export interface Composition {
    percentage: string;
    material: string;
}

export interface ImarketingMaterialDialogConfigs {
    heading: string | undefined;
    description: string | undefined;
}
const BuyboxView: React.FC<IBuyboxViewProps & IBuyboxExtensionProps<IBuyboxExtensionData>> = props => {
    const {
        ModuleProps,
        ProductInfoContainerProps,
        // MediaGalleryContainerProps,
        addToCart,
        addToOrderTemplate,
        addToWishlist,
        telemetryContent,
        configure,
        description,
        findInStore,
        quantity,
        price,
        title,
        rating,
        callbacks,
        state,
        resources,
        inventoryLabel,
        shopSimilarLook,
        keyInPrice,
        max,
        shopSimilarDescription,
        context,
        data: {
            product: { result: productData },
            productAttributes: { result: productAttributesData },
            productPrice: { result: productPriceData }
        },
        config: { pdfIconImage, priceLabelConfig, maximumLotQuantityMessage }
    } = props;
    // VSI Customization - Display product attributes
    // productPriceData?.DiscountLines
    const {
        segmentAttribute,
        collectionAttribute,
        colourCardAttribute,
        spinningTechAttribute,
        treatmentAttribute,
        countAttribute,
        twistAttribute,
        turnAttribute,
        micronAttribute,
        waxedAttribute,
        bobbianAttribute,
        guageAttribute,
        compositionAttribute,
        certificatesAttribute,
        careLabelAttribute
    } = useAttributeState(context, productAttributesData);
    const [colorCodeValue, setColorCode] = React.useState<string | undefined>();
    const [isLoading, setIsLoading] = React.useState(true);
    const [colorTypes, setColorTypes] = React.useState<(string | undefined)[]>([]);
    const [selectedColorType, setSelectedColorType] = React.useState('All');
    const [selectedTypeColors, setSelectedTypeColors] = React.useState<(number | undefined)[]>([]);
    const [colorImageUrl, setColorImageUrl] = React.useState('');
    const [isImageModalOpen, setIsImageModalOpen] = React.useState(false);
    const [inventoryData, setInventoryData] = React.useState<ISWGInventoryData[] | null>(null);
    const [totalAvailableInventory, setTotalAvailableInventory] = React.useState<number>(0);
    const [productDiscounts, setProductDiscounts] = React.useState<(string | undefined)[] | undefined>([]);
    const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);
    const tooltipRef = React.useRef<HTMLAnchorElement>(null);
    const kgPieceRef = React.useRef<HTMLAnchorElement>(null);
    const colortooltipRef = React.useRef<HTMLAnchorElement>(null);
    const toggleImageModal = () => {
        setIsImageModalOpen(!isImageModalOpen);
    };
    const getRequestQuantity = props.context.app.config.requestQuantity;
    const quantityThreshold = props.context.app.config.quantityThreshold;
    const upperQuantityThreshold = props.context.app.config.upperQuantityThreshold;
    const upperWeightThresholdKG = props.context.app.config.upperWeightThresholdKG;
    const kgPieceConfictWithInventory = productData?.DefaultUnitOfMeasure?.toLowerCase() === 'pcs' ? true : false;
    const displayThresholdMessage = !props.context.app.config.hideThresholdMessage;
    const quantityError = props.resources.bbOutOfStockErrorMessage;
    const weightCheckOnThreshold = getQuantityThreshold(state.quantity, upperWeightThresholdKG)
        ? quantityThreshold
        : upperQuantityThreshold;
    const percentageQuantity = kgPieceConfictWithInventory ? 0 : percentageValue(state.quantity, weightCheckOnThreshold);
    const fetchInventoryInfo = React.useCallback(async () => {
        const productColorDimension = productData?.Dimensions?.find((dimension: ProductDimension) => dimension.DimensionTypeValue === 1);
        const productColorDimensionCode = productColorDimension?.DimensionValue?.Value;
        if (productData && productData.RecordId && productColorDimensionCode) {
            if (inventoryData) {
                setInventoryData(null);
            }
            const inventory = await sWGGetInventoryDataAsync(
                { callerContext: props.context.actionContext },
                productData?.RecordId,
                // state.quantity
                getRequestQuantity
            );
            setInventoryData(inventory);
            let totalAvailable = inventory.reduce((total, data) => {
                return total + data.AvailPhysical;
            }, 0);
            totalAvailable = Math.floor(totalAvailable);
            setTotalAvailableInventory(totalAvailable);
            const totalValidQuantity = state.quantity + percentageQuantity;
            const productInventory = props.data.productAvailableQuantity.result?.find(inventory => inventory.deliveryType === 1);
            const availableQuantity = productInventory?.ProductAvailableQuantity.AvailableQuantity || 0;
            if (availableQuantity !== 0 && totalValidQuantity > Math.floor(availableQuantity)) {
                callbacks.updateErrorState({
                    errorHost: 'ADDTOCART',
                    quantityError: `${quantityError}`,
                    configureErrors: {},
                    customAmountError: undefined,
                    otherError: undefined
                });
            } else {
                callbacks.updateErrorState({
                    configureErrors: {}
                });
            }
            // if (totalAvailable !== 0 && state.quantity > totalAvailable) {
            //     callbacks.updateQuantity(totalAvailable);
            // }
        }
    }, [state.quantity, productData?.RecordId]);

    React.useEffect(() => {
        const productDimensions = props.data?.productDimensions;
        const result = productDimensions?.result;

        if (result && result.length > 0) {
            result.map(dimen => {
                if (props.callbacks?.initializeDimension) {
                    const size = dimen?.DimensionTypeValue.toString();
                    props.callbacks.initializeDimension(size);
                }
            });
        }
    }, [props.data?.productDimensions]);

    React.useEffect(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        if (state.quantity !== 0) {
            timeoutRef.current = setTimeout(() => {
                fetchInventoryInfo();
            }, 500);
        }
    }, [state.quantity, productData?.RecordId]);

    React.useEffect(() => {
        const productData = props.data.product.result;
        const productColorDimension = productData?.Dimensions?.find((dimension: ProductDimension) => dimension.DimensionTypeValue === 1);
        const productColorDimensionCode = productColorDimension?.DimensionValue?.Value;
        const productItemCode = productData?.ItemId && productData?.ItemId.replace(itemCodeRegex, '-');
        let productImage =
            productData?.ItemId &&
            generateImageUrl(`Products/${productItemCode}_000_001.png`, props.context.actionContext.requestContext.apiSettings);
        if (productColorDimensionCode) {
            productImage = generateImageUrl(
                `Color/${productColorDimensionCode}.png`,
                props.context.actionContext.requestContext.apiSettings
            );
        }
        productImage = `${productImage}&f=jpg`;
        setColorImageUrl(productImage || '');
    }, [colorCodeValue]);

    React.useEffect(() => {
        const productDimensionData = props.data.productDimensions.result;
        const colorDimension = productDimensionData?.find((dimension: ProductDimension) => dimension.DimensionTypeValue === 1);
        const colorType = colorDimension?.dimensionValuesWithInventory?.map(
            (dimension: ProductDimensionValueInventoryAvailability) => dimension.DimensionValue?.RefinerGroup?.split('-')[1]
        );
        if (colorType && colorType.length > 0) {
            setColorTypes([...new Set(colorType)]);
        }

        // get product discount lines
        const discountNames = productPriceData?.DiscountLines?.map(discountLine => {
            return discountLine.OfferName;
        });

        setProductDiscounts(discountNames);
    }, []);

    React.useEffect(() => {
        if (productAttributesData?.length) {
            setIsLoading(false);
        }
    }, [productAttributesData]);

    React.useEffect(() => {
        const productData = props.data.product.result;
        const productDimensionData = props.data.productDimensions.result;
        if (productData) {
            const productColorDimension = productData.Dimensions?.find((dimension: ProductDimension) => dimension.DimensionTypeValue === 1);
            const productColorDimensionCode = productColorDimension?.DimensionValue?.Value;
            const colorDimension = productDimensionData?.find((dimension: ProductDimension) => dimension.DimensionTypeValue === 1);
            const colorCodeWithType = colorDimension?.dimensionValuesWithInventory?.find(
                (dimension: ProductDimensionValueInventoryAvailability) =>
                    dimension.DimensionTypeValue === 1 && dimension.DimensionValue?.Value === productColorDimensionCode
            );
            const colorHex = colorCodeWithType?.DimensionValue?.RefinerGroup;
            colorHex && setColorCode(colorHex);
            callbacks.updateQuantity(1);
        }
    }, [props.data.product.result!.Dimensions]);

    // VSI Customization End
    React.useEffect(() => {
        if (selectedColorType !== 'All') {
            const productDimensionData = props.data.productDimensions.result;
            const colorDimension = productDimensionData?.find((dimension: ProductDimension) => dimension.DimensionTypeValue === 1);
            const filteredColorDimensions = colorDimension?.dimensionValuesWithInventory?.filter(dimension =>
                dimension.DimensionValue?.RefinerGroup?.includes(selectedColorType)
            );
            if (filteredColorDimensions && filteredColorDimensions.length > 0) {
                const filteredColorDimensionsRecIds = filteredColorDimensions.map(dimension => dimension.DimensionValue?.RecordId);
                setSelectedTypeColors(filteredColorDimensionsRecIds);
            }
        }
    }, [selectedColorType]);
    const handleColorTypeChange = (type: string) => {
        return (event: React.MouseEvent<HTMLButtonElement>) => {
            setSelectedColorType(type);
        };
    };

    const getCompositionValue = (compositionAttribute: AttributeValue | undefined): Composition[] => {
        if (!compositionAttribute || !compositionAttribute.TextValue) {
            return [];
        }

        return compositionAttribute.TextValue.split('|').map(value => {
            const [material, percentage] = value.trim().split(' ');
            return { material, percentage };
        });
    };

    const getCertificateImages = (): string[] => {
        if (certificatesAttribute) {
            const certificatesTextValue = certificatesAttribute.TextValue;
            if (certificatesTextValue) {
                const certificatesValue = certificatesTextValue.split(',').map((item: string) => item.trim().toLowerCase());
                const certificatesImages = certificatesValue.map((item: string) => {
                    const imageUrl = generateImageUrl(`${item}.png`, context.actionContext.requestContext.apiSettings);
                    return imageUrl?.replace('=/', '=') || '';
                });
                return certificatesImages;
            }
        }
        return [];
    };

    const certificatesImages = getCertificateImages();

    const getCareLabelImages = (): string[] => {
        if (careLabelAttribute) {
            const careLabelTextValue = careLabelAttribute.TextValue;
            if (careLabelTextValue) {
                const careLabelValue = careLabelTextValue.split('|').map((item: string) => item.trim().toLowerCase());
                const careLabelImages = careLabelValue.map((item: string) => {
                    const imageUrl = generateImageUrl(`${item}.png`, context.actionContext.requestContext.apiSettings);
                    return imageUrl?.replace('=/', '=') || '';
                });

                return careLabelImages.map(imageUrl => `${imageUrl}&w=40&h=40`);
            }
        }
        return [];
    };

    const careLabelImages = getCareLabelImages();

    const { allColorsButtonLabel } = resources;
    const { apiSettings } = props.context.request;
    // const imageData = {
    //     src: colorImageUrl,
    //     alt: 'product-image'
    // }
    const clonedTitle = React.cloneElement(title as JSX.Element, {
        text: productData?.Description
    });
    const { thresholdPrefix, thresholdContent, thresholdPostfix } = splitQuantityThresholdMessage(resources.quantityThresholdMessage);
    const { additionalWeightInfoLink, additionalWeightInfotooltip } = props.context.app.config;
    const {
        kgPieceConflictErrorMessage,
        kgPieceConflictErrorMessageLink,
        colorInfoIconTooltip,
        articleDetailIconLink,
        colorInfoIconLink
    } = props.config;
    const colorlabel = props.resources.colorLabel;
    const invalidQuantityError = props.resources.bbInvalidQuantityErrorMessage;
    const kgUnitCheck = productData?.DefaultUnitOfMeasure?.toLowerCase() !== 'pcs' ? true : false;
    const swgUnitOfMeasure = kgUnitCheck ? props.resources.swgUnitOfMeasure : props.resources.swgPieceUnitOfMeasure;
    const handlerichOutOfStockErrorDescriptionChange = (event: Msdyn365.ContentEditableEvent) =>
        (props.config.outOfStockErrorMessage = event.target.value);
    const handleKgPieceConflictErrorrichTextChange = (event: Msdyn365.ContentEditableEvent) =>
        (props.config.kgPieceConflictErrorMessage = event.target.value);
    const marketingMaterialconflictDialogHeading = props.config.marketingMaterialconflictDialogHeading;
    const marketingMaterialconflictDialogMessage = props.config.marketingMaterialconflictDialogMessage;
    const marketingMaterialDialogConfigs = {
        heading: marketingMaterialconflictDialogHeading,
        description: marketingMaterialconflictDialogMessage
    };
    const richOutOfStockDescription = props.config.outOfStockErrorMessage && (
        <Msdyn365.RichTextComponent
            text={props.config.outOfStockErrorMessage}
            editProps={{ onEdit: handlerichOutOfStockErrorDescriptionChange, requestContext: props.context.request }}
        />
    );
    const kgPieceConflictErrorrichText = kgPieceConflictErrorMessage && (
        <Msdyn365.RichTextComponent
            text={kgPieceConflictErrorMessage}
            editProps={{ onEdit: handleKgPieceConflictErrorrichTextChange, requestContext: props.context.request }}
        />
    );
    const cartLines = props.data.cart.result?.cart.CartLines;
    const newProductUnit = props.data.product.result?.DefaultUnitOfMeasure?.toLowerCase();
    const oldProductUnit = cartLines && cartLines.length > 0 ? cartLines[0]?.UnitOfMeasureSymbol?.toLowerCase() : undefined;
    let validateAddToCart = true;
    if (oldProductUnit && newProductUnit !== oldProductUnit) {
        validateAddToCart = false;
    }
    let bobbinImage = generateImageUrl(`bobbin_${bobbianAttribute?.TextValue?.replace('.', '-')}.png`, apiSettings);
    if (!bobbinImage) {
        bobbinImage = generateImageUrl(`bobbin_orange.png`, apiSettings);
    }
    bobbinImage = bobbinImage?.replace('=/', '=');
    const findAttributeTextValue = productAttributesData?.find(value => value.Name === 'ExtraInfo')?.TextValue;
    const highestConsumedQuantity = inventoryData && Math.max(...inventoryData.map(item => parseFloat(item.ConsumedQuantity!)));
    const showDescriptionValue = productAttributesData && productAttributesData?.find(data => data.Name === 'MM Description')?.TextValue;
    const showDisclaimerValue = productAttributesData && productAttributesData?.find(data => data.Name === 'MM Disclaimer')?.TextValue;
    const isCustomerSignedIn = context.app.config.enablePriceGuestUser ? props.context.request.user.isSignedIn : true;
    return (
        <Module {...ModuleProps}>
            {isLoading && (
                <div className='full-page-loader'>
                    <Waiting className='msc-waiting-circular msc-waiting-lg' />
                </div>
            )}
            <Node {...ProductInfoContainerProps}>
                {clonedTitle}
                {/* VSI Customization */}
                <div className='msc-product-data-specification'>
                    <div className='msc-product__data'>
                        <div className='msc-product__data-container'>
                            <div className='image_swatches-container'>
                                {colorImageUrl && colorImageUrl !== '' && (
                                    // <InlineZoomImageComponent image={imageData} imageZoomScale={imageZoomScale} index={0} context={context} id={props.id} className='msc-product__data-image'/>
                                    <Image
                                        onClick={toggleImageModal}
                                        requestContext={context.actionContext.requestContext}
                                        gridSettings={context.request.gridSettings!}
                                        src={colorImageUrl}
                                        loadFailureBehavior='hide'
                                        className='msc-product__data-image'
                                    />
                                )}
                                {isImageModalOpen && (
                                    <div className='fullscreen-preview'>
                                        <button onClick={toggleImageModal} className='close'></button>
                                        <div className='overlay' onClick={toggleImageModal}></div>
                                        <div className='content'>
                                            <img src={colorImageUrl} alt='product-image' />
                                        </div>
                                    </div>
                                )}
                                {/* <Node {...MediaGalleryContainerProps}>{props.mediaGallery}</Node> */}
                            </div>
                            {kgUnitCheck && (
                                <div className='product_detail-container'>
                                    <p className='color-required-message article-details'>
                                        <span>{resources.articleDetailLabel}</span>
                                        <a href={articleDetailIconLink?.destinationUrl} target='_blank' rel='noreferrer'>
                                            <i className='msi-InfoCircle' aria-hidden='true'></i>
                                        </a>
                                    </p>
                                    <ul className='msc-product__data-details'>
                                        <li className='segment'>
                                            <span>{resources.segmentLabel}</span>
                                            <span>{segmentAttribute?.TextValue}</span>
                                        </li>
                                        <li className='collection'>
                                            <span>{resources.collectionLabel}</span>
                                            <span>{collectionAttribute?.TextValue}</span>
                                        </li>
                                        <li className='article'>
                                            <span>{resources.articleLabel}</span>
                                            <span>{productData && productData.Name}</span>
                                        </li>
                                        {collectionAttribute && colourCardAttribute?.TextValue !== '' && (
                                            <li className='colour-card'>
                                                <span>{resources.colourCardLabel}</span>
                                                <span className='color-pdf'>
                                                    <a href={colourCardAttribute?.TextValue} target='_blank'>
                                                        {resources.viewColorCard}
                                                        <img alt='' className='pdf-icon' src={pdfIconImage?.src}></img>
                                                    </a>
                                                </span>
                                            </li>
                                        )}
                                        <li className='spinning'>
                                            <span>{resources.spinningLabel}</span>
                                            <span>{spinningTechAttribute?.TextValue}</span>
                                        </li>
                                        <li className='treatment'>
                                            <span>{resources.treatmentLabel}</span>
                                            <span className='treatment-values'>
                                                {treatmentAttribute &&
                                                    treatmentAttribute.TextValue?.split('|').map(treatment => {
                                                        return <div>{treatment}</div>;
                                                    })}
                                            </span>
                                        </li>
                                        <li className='waxed'>
                                            <span>{resources.waxedLabel}</span>
                                            <span>{waxedAttribute?.TextValue}</span>
                                        </li>
                                        {bobbianAttribute && (
                                            <li className='waxed'>
                                                <span>{resources.bobbin}</span>
                                                <span>
                                                    <Image
                                                        requestContext={context.actionContext.requestContext}
                                                        gridSettings={context.request.gridSettings!}
                                                        src={bobbinImage}
                                                        loadFailureBehavior='hide'
                                                        style={{ width: 25, height: 30 }}
                                                    />
                                                </span>
                                            </li>
                                        )}
                                    </ul>

                                    <div className='msc-product__properties'>
                                        <h3 className='msc-product__properties-heading'>{resources.fibreBlendHeading}</h3>
                                        <ul className='fibre-blend'>
                                            {getCompositionValue(compositionAttribute).map((value, i) => (
                                                <li className='composition' key={i}>
                                                    <span>{value.percentage}</span>
                                                    <span>{value.material}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    <div className='msc-product__properties'>
                                        <h3 className='msc-product__properties-heading'>{resources.fibrePropertiesHeading}</h3>
                                        <ul className='msc-product__fibre'>
                                            <li className='micron count'>
                                                <span>{resources.micronLabel}</span>
                                                {micronAttribute && <span className='micron-value'>{micronAttribute.TextValue}&#181;</span>}
                                            </li>
                                            <li className='count'>
                                                <span>{resources.countLabel}</span>
                                                <span>
                                                    {countAttribute?.TextValue} / {twistAttribute?.TextValue}
                                                </span>
                                            </li>
                                            <li className='turn'>
                                                <div>{resources.turnLabel}</div>
                                                <div>
                                                    <span className='turn'>{turnAttribute?.TextValue}</span>
                                                </div>
                                            </li>
                                            <li className='guage'>
                                                <div>{resources.guageLabel}</div>
                                                <span>{guageAttribute?.TextValue?.split('|')[0]}</span>
                                            </li>
                                        </ul>
                                    </div>
                                    {certificatesImages.length > 0 && (
                                        <>
                                            <span className='certificate-heading'>{resources.certificatesLabel}</span>
                                            <div className='certificates-images_container'>
                                                {certificatesImages.map((value, i) => (
                                                    <span key={i} className='certificate-img'>
                                                        <Image
                                                            requestContext={context.actionContext.requestContext}
                                                            gridSettings={context.request.gridSettings!}
                                                            src={value}
                                                            loadFailureBehavior='hide'
                                                        />
                                                    </span>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                    {careLabelImages.length > 0 && (
                                        <div className='msc-product__care-labels'>
                                            <h3 className='msc-product__care-labels-heading'>{resources.careLabel}</h3>
                                            <ul className='msc-product__care-labels-list'>
                                                {careLabelImages.map((value, i) => {
                                                    return (
                                                        <li key={i}>
                                                            <Image
                                                                requestContext={context.actionContext.requestContext}
                                                                gridSettings={context.request.gridSettings!}
                                                                src={value}
                                                                loadFailureBehavior='hide'
                                                            />
                                                            {/* <img src={value} alt='care-label' /> */}
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            )}
                            {showDescriptionValue && <RichTextComponent className='description-value' text={showDescriptionValue} />}
                        </div>
                        <p className='color-required-message color-diff-message'>
                            <a href={colorInfoIconLink?.destinationUrl} target='_blank' rel='noreferrer' ref={colortooltipRef}>
                                <i className='msi-InfoCircle' aria-hidden='true'></i>
                            </a>
                            {colorInfoIconTooltip && (
                                <UncontrolledTooltip target={colortooltipRef}>{colorInfoIconTooltip}</UncontrolledTooltip>
                            )}
                            <span>{resources.colorDifferenceMessage}</span>
                        </p>
                    </div>
                    <div className='msc-product__properties'>
                        {kgPieceConflictErrorrichText && !validateAddToCart && (
                            <p className='quantity-threshold-message'>
                                <a href={kgPieceConflictErrorMessageLink?.destinationUrl} target='_blank' rel='noreferrer' ref={kgPieceRef}>
                                    <i className='msi-InfoCircle'></i>
                                </a>
                                {additionalWeightInfotooltip && (
                                    <UncontrolledTooltip target={kgPieceRef}>{additionalWeightInfotooltip}</UncontrolledTooltip>
                                )}
                                <span>{kgPieceConflictErrorrichText}</span>
                            </p>
                        )}
                        
                        {/*  customization to show price for signIn user start */}
                        {context.app.config.enablePriceGuestUser && !context.request.user.isSignedIn ? (
                            <div className='price-text-signout-user_wrap'>
                            <p className='price-text-signout-user'>
                                {props.config.priceTextForGuestUser}{' '}
                            </p>
                             <a className='price-link-signout-user' href={props.config.signInUrl?.destinationUrl}>{props.config.sigInBtnText}</a>
                            </div>
                          
                        ) : (
                            <>
                                <h3 className='msc-product__properties-heading'>
                                    <span>{priceLabelConfig ? priceLabelConfig : resources.priceLabel}</span> {price}
                                </h3>
                            </>
                        )}
                        {/* customization end */}
                        {productDiscounts && (
                            <div className='product-discounts'>
                                {productDiscounts.map((discount, i) => {
                                    return <p key={i}>{discount}</p>;
                                })}
                            </div>
                        )}
                        {/* <div className='msc-product__properties-heading'>
                            <span>{resources.chooseColorTypeLabel}</span>
                        </div> */}
                        <div className='color-categories'>
                            <button
                                className={selectedColorType === allColorsButtonLabel ? 'active' : ''}
                                onClick={handleColorTypeChange(allColorsButtonLabel)}
                            >
                                {allColorsButtonLabel}
                            </button>
                            {colorTypes &&
                                colorTypes.length > 0 &&
                                colorTypes.map(type => {
                                    return (
                                        type &&
                                        type !== '' && (
                                            <button
                                                className={selectedColorType === type ? 'active' : ''}
                                                onClick={handleColorTypeChange(type || '')}
                                                key={type}
                                            >
                                                {type}
                                            </button>
                                        )
                                    );
                                })}
                        </div>
                        {configure &&
                            _renderConfigure(
                                configure,
                                resources,
                                selectedTypeColors,
                                apiSettings,
                                colorCodeValue,
                                selectedColorType,
                                colorlabel
                            )}
                        {quantity &&
                            _renderQuantity(
                                quantity,
                                callbacks,
                                props,
                                state,
                                resources,
                                max,
                                totalAvailableInventory,
                                telemetryContent,
                                colorCodeValue,
                                invalidQuantityError,
                                swgUnitOfMeasure
                            )}
                        {inventoryLabel}
                        {kgUnitCheck && quantity && displayThresholdMessage && (
                            <p className='quantity-threshold-message'>
                                <a href={additionalWeightInfoLink.destinationUrl} target='_blank' rel='noreferrer' ref={tooltipRef}>
                                    <i className='msi-InfoCircle'></i>
                                </a>
                                {additionalWeightInfotooltip && (
                                    <UncontrolledTooltip target={tooltipRef}>{additionalWeightInfotooltip}</UncontrolledTooltip>
                                )}
                                <span>
                                    {thresholdPrefix} {state.quantity} {thresholdContent} {percentageQuantity} {thresholdPostfix}
                                </span>
                            </p>
                        )}
                        {inventoryData && (
                            <ol className='inventory-information'>
                                {(state.productAvailableQuantity?.IsProductAvailable ||
                                    (state.productAvailableQuantity?.IsProductAvailable === undefined &&
                                        state.errorState.quantityError === undefined &&
                                        colorCodeValue !== undefined)) &&
                                    `${resources.inventoryLabel} | ${resources.makeupLabel}`}
                                {inventoryData.map((data, i) => {
                                    return (
                                        <li key={i}>
                                            <ul>
                                                {data.ConsumedQuantity && (
                                                    <li>
                                                        {resources.dyelotLabel}: {parseFloat(data.ConsumedQuantity).toFixed(2)}{' '}
                                                        {productData?.DefaultUnitOfMeasure}
                                                    </li>
                                                )}
                                                {data.PackingTypeName !== '' && <li>{data.PackingTypeName}</li>}
                                                {/* {data.BusinessModeValue !== '' && <li>{data.BusinessModeValue}</li>} */}
                                            </ul>
                                        </li>
                                    );
                                })}
                            </ol>
                        )}
                        <p className='maximum-lot-quantity-message'>
                            {((state.productAvailableQuantity?.IsProductAvailable &&
                                state.errorState.quantityError === undefined &&
                                highestConsumedQuantity !== null) ||
                                (state.productAvailableQuantity?.IsProductAvailable === undefined &&
                                    state.errorState.quantityError === undefined &&
                                    colorCodeValue !== undefined)) &&
                                `${maximumLotQuantityMessage} ${highestConsumedQuantity}`}
                        </p>
                        {_renderCartAndActions(
                            addToCart,
                            addToOrderTemplate,
                            addToWishlist,
                            colorCodeValue,
                            inventoryData,
                            resources.outOfStockText,
                            validateAddToCart,
                            state.errorState.quantityError,
                            marketingMaterialDialogConfigs,
                            isCustomerSignedIn
                        )}
                        {/* {(!colorCodeValue || colorCodeValue === '') && (
                            <p className='color-required-message'>
                                <i className='msi-InfoCircle' aria-hidden='true' />
                                {resources.productDimensionTypeColorErrorMessage}
                            </p>
                        )} */}
                        {inventoryData && inventoryData.length === 0 && (
                            <p className='color-required-message buybox-error-msg'>
                                <i className='msi-InfoCircle' aria-hidden='true' />
                                {richOutOfStockDescription}
                            </p>
                        )}
                        <RichTextComponent text={findAttributeTextValue} />
                        {showDisclaimerValue && <RichTextComponent className='disclaimer-value' text={showDisclaimerValue} />}
                        {/* <h3 className='msc-product__properties-heading'>{resources.availabilityHeading}</h3>
                        <ul className='availability'>
                            <li className='stock-service'>
                                <span>{resources.stockServiceLabel}</span>
                                <span className='value'>{stockSupportedAttributeValue}</span>
                            </li>
                            <li className='produced'>
                                <span>{resources.producedInLabel}</span>
                                <span className='value'>{availabilityAttributeValue}</span>
                            </li>
                        </ul> */}
                    </div>
                </div>

                {/* VSI Customization End */}
                {/* {price} */}
                {/* {unitOfMeasure} */}
                {props.bulkPurchaseLink}
                {description}
                {_renderTextBlock(props.slots.textBlocks)}
                {rating}
                {keyInPrice && _renderKeyInPrice(keyInPrice)}
                {findInStore && _renderFindInStore(findInStore)}
                {_renderSocialShare(props.slots && props.slots.socialShare)}
                {shopSimilarLook && _renderShopSimilarItem(shopSimilarLook)}
                {shopSimilarDescription && _renderShopSimilarItem(shopSimilarDescription)}
            </Node>
        </Module>
    );
};

const _renderTextBlock = (textBlocks: React.ReactNode[]): JSX.Element | undefined => {
    if (!textBlocks || textBlocks.length === 0) {
        return undefined;
    }

    return <>{textBlocks[0]}</>;
};

const _renderSocialShare = (socialShare: React.ReactNode[]): JSX.Element | undefined => {
    if (!socialShare || socialShare.length === 0) {
        return undefined;
    }

    return <>{socialShare[0]}</>;
};

const _renderCartAndActions = (
    addToCart?: IBuyboxAddToCartViewProps,
    addToOrderTemplate?: IBuyboxAddToOrderTemplateViewProps,
    addToWishlist?: IBuyboxAddToWishlistViewProps,
    colorCode?: string,
    inventoryData?: ISWGInventoryData[] | null,
    outOfStockText?: string,
    validateAddToCart?: boolean,
    quantityError?: string,
    marketingMaterialDialogConfigs?: ImarketingMaterialDialogConfigs,
    isCustomerSignedIn?: boolean
): JSX.Element | undefined => {
    if (!addToCart && !addToWishlist) {
        return undefined;
    }
    const addToCartEl = addToCart?.button as JSX.Element;
    const isAddToCartDisabled =
        validateAddToCart && colorCode && colorCode !== '' && inventoryData && inventoryData.length > 0 && !quantityError ? false : true;
    // Disable add to cart unless color is selected
    const addToCartButton = React.cloneElement(addToCart?.button as JSX.Element, {
        disabled: isAddToCartDisabled,
        addToCartText: inventoryData && inventoryData.length === 0 ? outOfStockText : addToCartEl.props.addToCartText,
        inventoryData: inventoryData
    });
    const addToOrderTemplateEl = addToOrderTemplate && (addToOrderTemplate?.button as JSX.Element);
    const addToOrderTemplateButton =
        addToOrderTemplateEl &&
        React.cloneElement(addToOrderTemplateEl as JSX.Element, {
            className: `${addToOrderTemplateEl.props.className} order-template-btn`,
            marketingMaterialDialogConfigs: marketingMaterialDialogConfigs
        });

        const buttonProps = get(addToCartButton, 'props');
    if (!isCustomerSignedIn) {
        set(buttonProps, 'disabled', true);
        set(buttonProps, 'isDisabled', true);
    }

    return (
        <div className='product-add-to-cart'>
            {addToCart && addToCart.errorBlock}
            {addToOrderTemplate && addToOrderTemplate.errorBlock}
            {addToWishlist && addToWishlist.errorBlock}
            <div className='buttons'>
                {addToCartButton}
                <div className='add-to-template-wrapper'>
                    {addToOrderTemplateButton && addToOrderTemplateButton}
                    <i className='pdp-template-icon'></i>
                </div>
                {addToWishlist && addToWishlist.button}
            </div>
        </div>
    );
};

const _renderConfigure = (
    configure: IBuyboxProductConfigureViewProps,
    resources: IBuyboxExtensionResources,
    selectedTypeColors: (number | undefined)[],
    apiSettings: ICommerceApiSettings,
    colorCode?: string,
    selectedColorType?: string,
    colorlabel?: string
): JSX.Element => {
    const { ContainerProps, dropdowns } = configure;

    return (
        <Node {...ContainerProps}>
            {dropdowns.map(dropdown =>
                _renderConfigureDropdown(dropdown, resources, selectedTypeColors, apiSettings, colorCode, selectedColorType, colorlabel)
            )}
        </Node>
    );
};

const _renderConfigureDropdown = (
    dropdown: IBuyboxProductConfigureDropdownViewProps,
    resources: IBuyboxExtensionResources,
    selectedTypeColors: (number | undefined)[],
    apiSettings: ICommerceApiSettings,
    colorCode?: string,
    selectedColorType?: string,
    colorlabel?: string
): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, errors, select } = dropdown;
    const colorCodeAndType = colorCode && colorCode.split('-');
    const colorValue = colorCodeAndType && colorCodeAndType[0];
    const colorType = colorCodeAndType && colorCodeAndType[1];
    const { colorTypeLabel, allColorsButtonLabel, productDimensionTypeColorErrorMessage } = resources;
    const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
    const [selectedColorCode, setSelectedColorCode] = React.useState('');
    const [selectedColorCodeImage, setSelectedColorCodeImage] = React.useState<string | undefined>();
    const initializeRef = React.useRef<boolean>(false);

    const ClonedHeading = React.cloneElement(heading as JSX.Element, {
        children: colorlabel
    });
    const selectEl = select as JSX.Element;
    const dropdownId = selectEl.props.dropdownId;
    const dropdownList: IDropdownItemProps[] = selectEl.props.dropdownList;
    let filteredDropdownList: IDropdownItemProps[] = dropdownList;
    if (dropdownId === '1' && selectedColorType !== allColorsButtonLabel) {
        filteredDropdownList = dropdownList.filter((item: IDropdownItemProps) => selectedTypeColors.includes(Number(item.id)));
    }

    React.useEffect(() => {
        if (colorValue) {
            setSelectedColorCode(colorValue || '');
        }
    }, [colorValue]);

    React.useEffect(() => {
        if (selectedColorCode !== '') {
            const swatchUrl = generateImageUrl(`Color/${selectedColorCode}.png` || '', apiSettings);
            setSelectedColorCodeImage(`${swatchUrl}&w=24&h=24`);
        }
    }, [selectedColorCode]);

    React.useEffect(() => {
        if (initializeRef.current && selectedColorType !== allColorsButtonLabel) {
            if (filteredDropdownList.length > 0) {
                const foundColor = filteredDropdownList.find(item => item.value === selectedColorCode);
                if (!foundColor) {
                    setSelectedColorCode('');
                }
            }
        } else {
            initializeRef.current = true;
        }
    }, [filteredDropdownList]);

    const handleColorChange = (item: IDropdownItemProps) => {
        return () => {
            selectEl.props.onChange({
                dropdownId: dropdownId,
                selectId: item.id,
                selectedValue: item.value
            });
            setSelectedColorCode(item.value);
            setIsDropdownOpen(false);
        };
    };

    const handleDropdownOpen = () => {
        setIsDropdownOpen(!isDropdownOpen);
    };
    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>
                {/* <div className='color-heading'>
                    {colorValue && colorValue !== '' && (
                        <button className='master-btn' onClick={toggleMasterImage}>
                            {showMasterImage ? viewColorImageButtonLabel : viewMasterImageButtonLabel}
                        </button>
                    )}
                </div> */}

                <div className='color-heading text'>
                    <span className='color-label'>{ClonedHeading}</span>
                    {colorValue && colorValue !== '' && <span>- {colorValue}</span>}
                    {colorType && <div className='color-label'>{colorTypeLabel}</div>}
                    {colorType && <span>- {colorType}</span>}
                </div>
                {errors}
            </Node>
            <div className='custom-dropdown-wrapper'>
                <div className='selected-dropdown-color' onClick={handleDropdownOpen}>
                    {selectedColorCode !== '' ? (
                        <>
                            <span className='swatch-wrap'>
                                <img alt='' src={selectedColorCodeImage} className='color-item-swatch' />
                            </span>
                            <span>{selectedColorCode}</span>
                        </>
                    ) : (
                        productDimensionTypeColorErrorMessage
                    )}
                </div>
                <ul className={isDropdownOpen ? 'opened' : ''}>
                    {filteredDropdownList &&
                        filteredDropdownList.map(item => {
                            let swatchUrl = generateImageUrl(item.imageUrl || '', apiSettings);
                            swatchUrl = `${swatchUrl}&w=24&h=24`;
                            return (
                                <li
                                    className={item.value === selectedColorCode ? 'selected' : ''}
                                    key={item.id}
                                    onClick={handleColorChange(item)}
                                >
                                    <span className='swatch-wrap'>
                                        <img src={swatchUrl} className='color-item-swatch' />
                                    </span>
                                    {item.value}
                                </li>
                            );
                        })}
                </ul>
                <div className={`overlay ${isDropdownOpen ? 'opened' : 'closed'}`} onClick={handleDropdownOpen}></div>
            </div>
            {/* {select} */}
        </Node>
    );
};

const _renderFindInStore = (findInStore: IBuyboxFindInStoreViewProps): JSX.Element => {
    const { ContainerProps, storeSelector, heading, description, errors, button, modal, productPickupOptionList } = findInStore;

    return (
        <Node {...ContainerProps}>
            {storeSelector}
            {heading}
            {productPickupOptionList}
            {description}
            {errors}
            {button}
            {modal}
        </Node>
    );
};

/**
 * On Change function.
 * @param callbacks -Callbacks.
 * @returns Update quantity.
 */

const zeroQuantityError = (callbacks: IBuyboxCallbacks, props: IBuyboxProps<IBuyboxData>) => (newError: IErrorState): void => {
    return callbacks.updateErrorState(newError);
};

const onChangeHandler = (callbacks: IBuyboxCallbacks, props: IBuyboxProps<IBuyboxData>) => (newValue: number): boolean => {
    // const quantityThreshold = props.context.app.config.quantityThreshold;
    // Inventory for shipping
    // const productInventory = props.data.productAvailableQuantity.result?.find(inventory => inventory.deliveryType === 1);
    // const availableQuantity = productInventory?.ProductAvailableQuantity.AvailableQuantity || 0;
    // const quantityToAdd = percentageValue(newValue, quantityThreshold);
    // const maxQuantity = newValue + quantityToAdd;
    // if(maxQuantity > availableQuantity) return false;

    if (callbacks.updateQuantity) {
        return callbacks.updateQuantity(newValue);
    }
    return true;
};

const _renderQuantity = (
    quantityComponent: IBuyboxProductQuantityViewProps,
    callbacks: IBuyboxCallbacks,
    props: IBuyboxProps<IBuyboxData>,
    state: IBuyboxState,
    extentionResources: IBuyboxExtensionResources,
    // quantityLimitsMessages: React.ReactNode,
    max: number | undefined,
    totalAvailableInventory: number,
    telemetryContent?: ITelemetryContent,
    colorCode?: string,
    invalidQuantityError?: string,
    swgUnitOfMeasure?: string
): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, errors } = quantityComponent;

    const { resources } = props;

    const { quantity } = state;

    const customUnitOfMeasureHeading = React.cloneElement(heading as JSX.Element, {
        children: swgUnitOfMeasure || 'KG'
    });
    // let maxInventory = max;
    // const quantityThreshold = props.context.app.config.quantityThreshold;
    // Inventory for shipping
    // const productInventory = props.data.productAvailableQuantity.result?.find(inventory => inventory.deliveryType === 1);
    // const availableQuantity = productInventory?.ProductAvailableQuantity.AvailableQuantity || 0;
    // if(productInventory) {
    //     const maxQuantityToSubtract = percentageValue(availableQuantity, quantityThreshold);
    //     maxInventory = availableQuantity - maxQuantityToSubtract;
    //     maxInventory = Math.round(maxInventory);
    // }

    // const maxInventory = quantity <= totalAvailableInventory ? max : totalAvailableInventory;
    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>
                {customUnitOfMeasureHeading}
                {errors}
            </Node>

            <IncrementalQuantity
                id='ms-buybox__product-quantity-input'
                max={max}
                currentCount={quantity}
                onChange={onChangeHandler(callbacks, props)}
                quantityError={zeroQuantityError(callbacks, props)}
                inputQuantityAriaLabel={resources.inputQuantityAriaLabel}
                decrementButtonAriaLabel={extentionResources.decrementButtonAriaLabel}
                incrementButtonAriaLabel={extentionResources.incrementButtonAriaLabel}
                minQuantityText={extentionResources.minQuantityText}
                maxQuantityText={extentionResources.maxQuantityText}
                telemetryContent={telemetryContent}
                invalidQuantityError={invalidQuantityError}
                disabled={colorCode && colorCode !== '' && totalAvailableInventory !== 0 ? false : true}
            />
            {/* {quantityLimitsMessages} */}
        </Node>
    );
};

const _renderKeyInPrice = (keyInPrice: IBuyboxKeyInPriceViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, input } = keyInPrice;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>
            {input}
        </Node>
    );
};

const _renderShopSimilarItem = (shopSimilarItem: IBuyboxShopSimilarLookViewProps): JSX.Element => {
    const { ContainerProps, errors, input } = shopSimilarItem;

    return (
        <Node {...ContainerProps}>
            {errors}
            {input}
        </Node>
    );
};

export default BuyboxView;
